// ============================================================
// GitTroubleshooting.jsx
//
// A branded, responsive course content page for the
// "Git Troubleshooting & Recovery" lesson.
//
// Author: ProDev Enterprise Series (DevOps Branch)
// Features: Merge conflict resolution, Detached HEAD recovery,
//           Reflog time-travel, and merge abortion safety.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import ConstructionIcon       from "@mui/icons-material/Construction";
import PsychologyIcon         from "@mui/icons-material/Psychology";
import HistoryIcon            from "@mui/icons-material/History";
import WarningAmberIcon       from "@mui/icons-material/WarningAmber";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import BugReportIcon          from "@mui/icons-material/BugReport";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function GitTroubleshooting() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<ConstructionIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="DevOps · Recovery & Repair"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Troubleshooting <span>& Recovery</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Git is built to ensure you never lose code. From messy merge conflicts to "Detached HEAD" states, these advanced recovery tools act as your developer safety net, ensuring your project remains intact through any storm.
                    </Typography>
                </Box>

                {/* 1. MERGE CONFLICTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<WarningAmberIcon />} label="1. Resolving Merge Conflicts" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Conflicts occur when two branches modify the same line. Git marks the collision in your files—resolve them manually, stage, and complete the snapshot.
                    </Typography>
                    <CodeBlock
                        title="Conflict Workflow"
                        code={`# 1. Identify files\ngit status\n\n# 2. Open file and choose change\n<<<<<<< HEAD (Local)\n=======\n>>>>>>> feature (Incoming)\n\n# 3. Finalize\ngit add <file>\ngit commit -m "fix: resolve merge conflicts"`}
                    />
                </Box>

                {/* 2. DETACHED HEAD */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<PsychologyIcon />} label="2. The 'Detached HEAD' State" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        This happens when you checkout a specific commit hash rather than a branch. Changes made here aren't linked to a branch and can be lost if you switch away.
                    </Typography>
                    <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                        <Typography sx={{ color: BRAND.white, fontSize: 13, mb: 2 }}>
                            To save work created in a detached state, simply create a new branch immediately:
                        </Typography>
                        <CodeBlock
                            title="Safe Checkout"
                            code={`git switch -c recovery-branch-name\n# Now your HEAD is attached to a real branch again!`}
                        />
                    </Paper>
                </Box>

                {/* 3. REFLOG TIME MACHINE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<HistoryIcon />} label="3. Recovering Lost Commits (Reflog)" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Did you delete a branch or perform a <code>--hard reset</code> by mistake? <code>git reflog</code> is the ultimate audit trail, tracking every movement of the HEAD pointer.
                    </Typography>
                    <CodeBlock
                        title="Reflog Recovery"
                        code={`# View all recent HEAD movements\ngit reflog\n\n# Jump back to a specific state (e.g., position 2)\ngit reset --hard HEAD@{2}\n\n# Or checkout into a fresh branch to be safe\ngit checkout -b restored-work HEAD@{5}`}
                    />
                </Box>

                {/* 4. ABORTING MERGES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<BugReportIcon />} label="4. Emergency Exit: --abort" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        If a merge or rebase becomes too overwhelming, you can always hit the "Panic Button" to return the repository to its exact state before the operation started.
                    </Typography>
                    <CodeBlock
                        title="Operation Abortion"
                        code={`# Cancel an active merge\ngit merge --abort\n\n# Cancel an active rebase\ngit rebase --abort`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* RECOVERY HIGHLIGHTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Troubleshooting Checklist" />
                    <Grid container spacing={1.5}>
                        {[
                            "Run 'git status' first whenever things feel weird.",
                            "Use 'git log --oneline --graph' to visualize where your HEAD is.",
                            "Don't panic if you lose a commit; it's almost certainly in 'git reflog'.",
                            "Always create a backup branch before attempting complex rebases.",
                            "Use a GUI tool (like VS Code's merge editor) for complex conflicts.",
                            "Check 'git diff' to see exactly what is causing a conflict."
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/devops-hub"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": { borderColor: BRAND.goldBright, background: BRAND.goldGlow }
                        }}
                    >
                        Return to DevOps Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Stressed about merge markers? You can use <code>git checkout --ours &lt;file&gt;</code> or <code>git checkout --theirs &lt;file&gt;</code> to quickly resolve a conflict by picking one side entirely!
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
